import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import { useShopifyProducts } from "../../hooks/useShopifyProducts";
import { ShopifyProduct } from "../../types/shopify";
import { getHeroFeaturedProducts } from "../../utils/productFeaturedSelection";
import { formatPrice } from "../../utils/shopifyHelpers";
import { LoadingSkeleton } from "../ui/LoadingStates";
import logoImage from "../../assets/logos/e-logo-trans-40.png";

const DEFAULT_STORE_WIDGET_PRODUCT_COUNT = 4;

interface StoreWidgetContentProps {
  count?: number;
}

const getProductImage = (product: ShopifyProduct): string | undefined =>
  product.images?.edges?.[0]?.node?.url;

const getProductPrice = (product: ShopifyProduct): string | null => {
  const price = product.priceRange?.minVariantPrice;

  if (!price) {
    return null;
  }

  return formatPrice(parseFloat(price.amount), price.currencyCode);
};

const StoreWidgetContentInner: React.FC<StoreWidgetContentProps> = ({
  count = DEFAULT_STORE_WIDGET_PRODUCT_COUNT,
}) => {
  const {
    data: products = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useShopifyProducts({
    first: 50,
    useCase: "homepage-store-widget",
  });

  const featuredProducts = useMemo(
    () => getHeroFeaturedProducts(products, count),
    [products, count]
  );

  if (isLoading) {
    return (
      <>
        <div className="mb-4 sm:mb-6 flex items-center justify-between">
          <LoadingSkeleton height="h-8" width="w-40" className="mb-2" />
          <LoadingSkeleton height="h-5" width="w-24" />
        </div>
        <div className="flex gap-3 sm:gap-4 overflow-hidden">
          {Array.from({ length: Math.max(4, count) }).map((_, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-40 sm:w-[13.5rem] space-y-2"
            >
              <LoadingSkeleton height="h-40" width="w-full" />
              <LoadingSkeleton height="h-4" width="w-full" />
              <LoadingSkeleton height="h-3" width="w-2/3" />
            </div>
          ))}
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-red-500/20 bg-red-950/20 p-4 sm:p-5"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Store</h2>
            <p className="mt-1 text-sm text-slate-300">
              Store picks are temporarily unavailable
              {error?.message ? `: ${error.message}` : "."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm font-medium text-red-300 hover:text-red-200 transition-colors"
            >
              Retry
            </button>
            <Link
              to="/store"
              className="text-sm font-medium text-red-400 hover:text-red-300 transition-colors"
            >
              Open Store
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!featuredProducts.length) {
    return null;
  }

  return (
    <>
      <div className="mb-4 sm:mb-6 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-red-400">
            Merch
          </p>
          <h2 className="mt-1 text-xl sm:text-2xl font-bold text-white">Store</h2>
        </div>
        <Link
          to="/store"
          className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors flex items-center"
        >
          Shop Store
          <span aria-hidden="true" className="ml-1">
            &gt;
          </span>
        </Link>
      </div>

      <ul
        className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:overflow-visible scrollbar-hide list-none"
        aria-label="Featured store products"
      >
        {featuredProducts.map((product) => (
          <li
            key={product.id}
            className="flex-shrink-0 w-40 sm:w-auto"
          >
            <StoreProductCard product={product} />
          </li>
        ))}
      </ul>
    </>
  );
};

const StoreProductCard: React.FC<{ product: ShopifyProduct }> = ({ product }) => {
  const imageUrl = getProductImage(product);
  const price = getProductPrice(product);

  return (
    <Link
      to="/store"
      className="group relative block overflow-hidden rounded-xl border border-gray-700/50 bg-gray-900/70 shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:border-red-500/40 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:ring-offset-2 focus:ring-offset-gray-900"
      aria-label={`Shop ${product.title} in the store`}
    >
      <div className="relative aspect-square bg-gradient-to-br from-gray-800 via-gray-900 to-black">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <img
              src={logoImage}
              alt=""
              aria-hidden="true"
              className="h-12 w-12 object-contain opacity-70"
              loading="lazy"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
        <span className="absolute left-3 top-3 rounded-full bg-red-500 px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-wider text-white shadow-lg">
          Featured
        </span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-red-300">
          {product.productType || "Merch"}
        </p>
        <h3 className="mt-1 line-clamp-2 text-sm sm:text-base font-semibold leading-tight text-white transition-colors group-hover:text-red-200">
          {product.title}
        </h3>
        {price && (
          <p className="mt-2 text-sm font-bold text-white">
            {price}
          </p>
        )}
      </div>
    </Link>
  );
};

const QueryClientBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [client] = useState(() => new QueryClient());

  try {
    useQueryClient();
    return <>{children}</>;
  } catch {
    return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
  }
};

const StoreWidgetContent: React.FC<StoreWidgetContentProps> = (props) => (
  <QueryClientBoundary>
    <StoreWidgetContentInner {...props} />
  </QueryClientBoundary>
);

export default StoreWidgetContent;
